import React, { useEffect, useRef, useState } from "react";

/**
 * Reveal — wraps children and fades + slides them in when scrolled into view.
 *
 * Props:
 *  - direction: "up" | "down" | "left" | "right" | "fade"  (default: "up")
 *  - delay:    milliseconds before animation starts        (default: 0)
 *  - duration: milliseconds for the animation              (default: 700)
 *  - distance: px the element travels                      (default: 24)
 *  - threshold: 0..1, how much must be visible to trigger  (default: 0.15)
 *  - once: animate only once                               (default: true)
 *  - className: extra classes for the wrapper
 *  - as: HTML element tag                                  (default: "div")
 */
const Reveal = ({
  children,
  direction = "up",
  delay = 0,
  duration = 700,
  distance = 24,
  threshold = 0.15,
  once = true,
  className = "",
  as: Tag = "div",
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Respect users who prefer reduced motion
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) observer.unobserve(node);
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, once]);

  const offset = (() => {
    switch (direction) {
      case "up":    return `translate3d(0, ${distance}px, 0)`;
      case "down":  return `translate3d(0, -${distance}px, 0)`;
      case "left":  return `translate3d(${distance}px, 0, 0)`;
      case "right": return `translate3d(-${distance}px, 0, 0)`;
      default:      return "translate3d(0, 0, 0)";
    }
  })();

  const style = {
    opacity: visible ? 1 : 0,
    transform: visible ? "translate3d(0, 0, 0)" : offset,
    transition: `opacity ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
    willChange: "opacity, transform",
  };

  return (
    <Tag ref={ref} style={style} className={className}>
      {children}
    </Tag>
  );
};

export default Reveal;
